/** @format */

import React, { useState, useCallback } from 'react';
import {
  AcademicCapIcon,
  DocumentArrowDownIcon,
  UserIcon,
  CalendarIcon,
  StarIcon
} from '@heroicons/react/24/outline';
import logo from '../assets/icon.png';

interface CertificateProps {
  userName: string;
  courseName: string;
  completionDate: Date;
  score: number;
  totalQuestions: number;
  certificateId: string;
}

interface CertificateGeneratorProps {
  assessmentData: {
    score: number;
    totalQuestions: number;
    courseName: string;
    completionDate: Date;
  };
  onClose: () => void;
}

const CertificateGenerator: React.FC<CertificateGeneratorProps> = ({
  assessmentData,
  onClose
}) => {
  const [userName, setUserName] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);

  // Generate certificate PDF
  const generateCertificate = useCallback(async () => {
    if (!userName.trim()) {
      alert('Please enter your name');
      return;
    }

    setIsGenerating(true);

    try {
      // Create certificate content
      const certificateData: CertificateProps = {
        userName: userName.trim(),
        courseName: assessmentData.courseName,
        completionDate: assessmentData.completionDate,
        score: assessmentData.score,
        totalQuestions: assessmentData.totalQuestions,
        certificateId: `CERT-${Date.now()}`
      };

      // Generate certificate as HTML and convert to PDF
      const certificateHTML = generateCertificateHTML(certificateData, logo);
      
      // Create a new window for printing
      const printWindow = window.open('', '_blank');
      if (printWindow) {
        printWindow.document.write(certificateHTML);
        printWindow.document.close();
        
        // Wait for content to load then trigger print
        setTimeout(() => {
          printWindow.print();
          printWindow.close();
        }, 1000);
      }

      // Also create a downloadable version
      const blob = new Blob([certificateHTML], { type: 'text/html' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Certificate_${userName.replace(/\s+/g, '_')}_${Date.now()}.html`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);

    } catch (error) {
      console.error('Error generating certificate:', error);
      alert('Failed to generate certificate. Please try again.');
    } finally {
      setIsGenerating(false);
    }
  }, [userName, assessmentData]);

  // Generate certificate HTML
  const generateCertificateHTML = (data: CertificateProps, logoSrc: string): string => {
    return `
<!DOCTYPE html>
<html lang="en">
<head>
    <meta charset="UTF-8">
    <meta name="viewport" content="width=device-width, initial-scale=1.0">
    <title>Certificate of Achievement</title>
    <style>
        @page {
            size: A4 landscape;
            margin: 0;
        }
        
        body {
            font-family: 'Georgia', serif;
            margin: 0;
            padding: 40px;
            background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
            min-height: 100vh;
            display: flex;
            align-items: center;
            justify-content: center;
        }
        
        .certificate {
            background: white;
            width: 800px;
            height: 600px;
            padding: 60px;
            box-shadow: 0 20px 40px rgba(0,0,0,0.1);
            border: 8px solid #2563eb;
            position: relative;
            text-align: center;
        }
        
        .certificate::before {
            content: '';
            position: absolute;
            top: 20px;
            left: 20px;
            right: 20px;
            bottom: 20px;
            border: 2px solid #3b82f6;
            pointer-events: none;
        }
        
        .header {
            margin-bottom: 40px;
        }
        
        .title {
            font-size: 48px;
            color: #1e40af;
            font-weight: bold;
            margin-bottom: 10px;
            text-transform: uppercase;
            letter-spacing: 3px;
        }
        
        .subtitle {
            font-size: 24px;
            color: #64748b;
            margin-bottom: 40px;
        }
        
        .recipient {
            margin: 40px 0;
        }
        
        .presented-to {
            font-size: 18px;
            color: #64748b;
            margin-bottom: 15px;
        }
        
        .name {
            font-size: 42px;
            color: #1e40af;
            font-weight: bold;
            margin: 20px 0;
            border-bottom: 3px solid #3b82f6;
            display: inline-block;
            padding-bottom: 10px;
            min-width: 400px;
        }
        
        .achievement {
            font-size: 20px;
            color: #374151;
            margin: 30px 0;
            line-height: 1.6;
        }
        
        .course-name {
            color: #1e40af;
            font-weight: bold;
        }
        
        .score {
            background: #22c55e;
            color: white;
            padding: 8px 16px;
            border-radius: 20px;
            font-weight: bold;
            display: inline-block;
            margin: 0 5px;
        }
        
        .footer {
            display: flex;
            justify-content: space-between;
            align-items: center;
            margin-top: 60px;
            font-size: 14px;
            color: #64748b;
        }
        
        .date {
            text-align: left;
        }
        
        .certificate-id {
            text-align: right;
        }
        
        .signature-line {
            border-top: 2px solid #64748b;
            width: 200px;
            margin: 20px auto 10px;
        }
        
        .signature-title {
            font-size: 12px;
            color: #64748b;
        }
        
        .logo {
            position: absolute;
            top: 30px;
            right: 30px;
            width: 64px;
            height: 64px;
        }
        .logo img {
            width: 64px;
            height: 64px;
            object-fit: contain;
            display: block;
        }
        
        @media print {
            body {
                background: white;
            }
            
            .certificate {
                box-shadow: none;
                width: 100%;
                height: 100vh;
                max-width: none;
            }
        }
    </style>
</head>
<body>
    <div class="certificate">
        <div class="logo"><img src="${logoSrc}" alt="Logo" /></div>
        
        <div class="header">
            <div class="title">Certificate</div>
            <div class="subtitle">of Achievement</div>
        </div>
        
        <div class="recipient">
            <div class="presented-to">This is to certify that</div>
            <div class="name">${data.userName}</div>
        </div>
        
        <div class="achievement">
            has successfully completed the <span class="course-name">${data.courseName}</span> assessment
            <br>
            with a score of <span class="score">${data.score}%</span> 
            (${Math.round((data.score / 100) * data.totalQuestions)} out of ${data.totalQuestions} questions correct)
            <br><br>
            demonstrating proficiency and dedication to learning
        </div>
        
        <div class="signature-line"></div>
        <div class="signature-title">ProfStudy Learning Platform</div>
        
        <div class="footer">
            <div class="date">
                <strong>Date:</strong><br>
                ${data.completionDate.toLocaleDateString('en-US', {
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric'
                })}
            </div>
            <div class="certificate-id">
                <strong>Certificate ID:</strong><br>
                ${data.certificateId}
            </div>
        </div>
    </div>
</body>
</html>`;
  };

  const passThreshold = 70; // 70% to pass
  const isPassing = assessmentData.score >= passThreshold;

  if (!isPassing) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
        <div className="bg-white rounded-lg p-6 max-w-md w-full">
          <div className="text-center">
            <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100 mb-4">
              <StarIcon className="h-6 w-6 text-red-600" />
            </div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              Certificate Not Available
            </h3>
            <p className="text-sm text-gray-500 mb-4">
              You need a score of {passThreshold}% or higher to receive a certificate. 
              Your current score is {assessmentData.score}%.
            </p>
            <p className="text-sm text-gray-600 mb-6">
              Keep practicing and try again to earn your certificate!
            </p>
            <button
              onClick={onClose}
              className="w-full inline-flex justify-center rounded-md border border-transparent bg-primary-600 px-4 py-2 text-base font-medium text-white shadow-sm hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg p-6 max-w-md w-full">
        <div className="text-center">
          <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-green-100 mb-4">
            <AcademicCapIcon className="h-6 w-6 text-green-600" />
          </div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            Congratulations! 🎉
          </h3>
          <p className="text-sm text-gray-500 mb-4">
            You've earned a certificate with a score of {assessmentData.score}%!
          </p>
          
          {/* Score Details */}
          <div className="bg-gray-50 rounded-lg p-4 mb-6">
            <div className="flex items-center justify-between text-sm">
              <span className="text-gray-600">Score:</span>
              <span className="font-semibold text-green-600">{assessmentData.score}%</span>
            </div>
            <div className="flex items-center justify-between text-sm mt-2">
              <span className="text-gray-600">Correct Answers:</span>
              <span className="font-semibold">
                {Math.round((assessmentData.score / 100) * assessmentData.totalQuestions)} / {assessmentData.totalQuestions}
              </span>
            </div>
            <div className="flex items-center justify-between text-sm mt-2">
              <span className="text-gray-600">Date:</span>
              <span className="font-semibold">
                {assessmentData.completionDate.toLocaleDateString()}
              </span>
            </div>
          </div>

          {/* Name Input */}
          <div className="mb-6">
            <label htmlFor="certificate-name" className="block text-sm font-medium text-gray-700 mb-2">
              Enter your full name for the certificate:
            </label>
            <div className="relative">
              <UserIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
              <input
                type="text"
                id="certificate-name"
                value={userName}
                onChange={(e) => setUserName(e.target.value)}
                placeholder="Your Full Name"
                className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              />
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex space-x-3">
            <button
              onClick={onClose}
              className="flex-1 inline-flex justify-center rounded-md border border-gray-300 bg-white px-4 py-2 text-base font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2"
            >
              Cancel
            </button>
            <button
              onClick={generateCertificate}
              disabled={isGenerating || !userName.trim()}
              className="flex-1 inline-flex justify-center items-center rounded-md border border-transparent bg-primary-600 px-4 py-2 text-base font-medium text-white shadow-sm hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isGenerating ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Generating...
                </>
              ) : (
                <>
                  <DocumentArrowDownIcon className="h-4 w-4 mr-2" />
                  Generate Certificate
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CertificateGenerator;
